/* A&Y — Fiche produit : galerie (vignettes, zoom, défilement doux), variantes en pastilles,
   synchronisation prix / stock / bouton, bouton d'achat fixe mobile, mot cadeau, avis, description, Sleek. */
(() => {
  'use strict';
  const root = document.querySelector('[data-ay-product]');
  if (!root) return;
  const config = JSON.parse(root.querySelector('[data-product-config]').textContent);
  const variants = JSON.parse(root.querySelector('[data-product-variants-json]').textContent);
  const labels = config.labels;
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const reducedNow = () => motion.matches || document.documentElement.dataset.ayReduced === 'true';
  const formatPrice = cents => window.ayMoney ? window.ayMoney(cents) : (cents / 100).toFixed(2).replace('.', ',') + ' $';

  /* ------------------------------------------------------------------ */
  /* Galerie                                                             */
  /* ------------------------------------------------------------------ */
  const carousel = (() => {
    const gallery = root.querySelector('.ay-pdp__gallery');
    const track = gallery.querySelector('.ay-media-list');
    const allSlides = [...track.querySelectorAll('.ay-media-item[data-media-id]')];
    const allThumbs = [...gallery.querySelectorAll('[data-gallery-thumb]')];
    let slides = allSlides.filter(slide => !slide.hidden);
    let images = slides.filter(slide => slide.dataset.mediaType === 'image');
    const controls = gallery.querySelector('[data-gallery-controls]');
    const stopButton = gallery.querySelector('[data-gallery-stop]');
    const counter = gallery.querySelector('[data-gallery-counter]');
    const controller = new AbortController();
    let stopped = true;
    let focused = false;
    let index = 0;
    let timer = 0;
    let frame = 0;
    let destroyed = false;
    let inView = true;
    const on = (element, event, callback, options = {}) => element && element.addEventListener(event, callback, { ...options, signal: controller.signal });
    const clearTimer = () => { window.clearTimeout(timer); timer = 0; };
    function refreshSlides() {
      slides = allSlides.filter(slide => !slide.hidden);
      images = slides.filter(slide => slide.dataset.mediaType === 'image');
      if (controls) controls.hidden = slides.length < 2;
    }
    function filterVariantMedia(group) {
      if (!group) return;
      const grouped = allSlides.filter(slide => (slide.dataset.mediaGroup || 'general') !== 'general');
      if (!grouped.length) return;
      const generalSlides = allSlides.filter(slide => (slide.dataset.mediaGroup || 'general') === 'general').slice(0, 5);
      const allowed = new Set(generalSlides.map(slide => slide.dataset.mediaId));
      allSlides.forEach(slide => { const mediaGroup = slide.dataset.mediaGroup || 'general'; slide.hidden = !(allowed.has(slide.dataset.mediaId) || mediaGroup === group); });
      allThumbs.forEach(thumb => { const mediaGroup = thumb.dataset.mediaGroup || 'general'; thumb.hidden = !(allowed.has(thumb.dataset.mediaId) || mediaGroup === group); });
      refreshSlides();
      index = 0;
      updatePosition(0);
      track.scrollTo({ left: 0, behavior: 'instant' });
    }
    function updateControls() {
      if (!stopButton) return;
      const blocked = reducedNow() || images.length < 2;
      stopButton.textContent = blocked ? labels.reduced : stopped ? labels.resume : labels.stop;
      stopButton.setAttribute('aria-disabled', String(blocked));
      stopButton.hidden = blocked;
      counter.setAttribute('aria-live', stopped || blocked ? 'polite' : 'off');
    }
    function updatePosition(nextIndex) {
      if (!slides.length) return;
      index = Math.max(0, Math.min(nextIndex, slides.length - 1));
      const text = (index + 1) + ' / ' + slides.length;
      if (counter.textContent !== text) { counter.textContent = text; counter.setAttribute('aria-label', labels.position.replace('__current__', index + 1).replace('__total__', slides.length)); }
      slides.forEach((slide, slideIndex) => {
        slide.inert = slideIndex !== index;
        if (slideIndex !== index) slide.querySelectorAll('video').forEach(video => { if (!video.paused) video.pause(); });
      });
      const currentId = slides[index].dataset.mediaId;
      allThumbs.forEach(thumb => {
        const active = thumb.dataset.mediaId === currentId;
        if (active) { thumb.setAttribute('aria-current', 'true'); thumb.scrollIntoView?.({ block: 'nearest', behavior: reducedNow() ? 'instant' : 'smooth' }); } else thumb.removeAttribute('aria-current');
      });
    }
    function nearestIndex() {
      const left = track.getBoundingClientRect().left;
      let nearest = 0, distance = Infinity;
      slides.forEach((slide, slideIndex) => { const d = Math.abs(slide.getBoundingClientRect().left - left); if (d < distance) { distance = d; nearest = slideIndex; } });
      return nearest;
    }
    function goTo(nextIndex, animate = true) {
      if (!slides.length) return;
      const normalized = (nextIndex + slides.length) % slides.length;
      const smooth = animate && !reducedNow();
      if (!smooth) updatePosition(normalized);
      const left = slides[normalized].offsetLeft - slides[0].offsetLeft;
      track.scrollTo({ left, behavior: smooth ? 'smooth' : 'instant' });
    }
    function schedule() {
      clearTimer();
      if (destroyed || stopped || focused || reducedNow() || document.hidden || !inView || !root.isConnected || images.length < 2 || slides[index]?.dataset.mediaType !== 'image') return;
      timer = window.setTimeout(() => {
        if (stopped || focused || reducedNow() || document.hidden || !inView) return;
        const imageIndex = images.indexOf(slides[index]);
        const next = images[(imageIndex + 1) % images.length];
        goTo(slides.indexOf(next), true);
        schedule();
      }, 6000);
    }
    on(gallery, 'focusin', event => { focused = event.target !== stopButton; schedule(); });
    on(gallery, 'focusout', event => { focused = gallery.contains(event.relatedTarget) && event.relatedTarget !== stopButton; schedule(); });
    on(track, 'pointerdown', clearTimer, { passive: true });
    on(track, 'pointerup', schedule, { passive: true });
    on(stopButton, 'click', () => { if (reducedNow() || images.length < 2) return; stopped = !stopped; if (stopped) clearTimer(); updateControls(); schedule(); });
    on(gallery.querySelector('[data-gallery-previous]'), 'click', () => { goTo(index - 1); schedule(); });
    on(gallery.querySelector('[data-gallery-next]'), 'click', () => { goTo(index + 1); schedule(); });
    allThumbs.forEach(thumb => on(thumb, 'click', () => { const mediaIndex = slides.findIndex(slide => slide.dataset.mediaId === thumb.dataset.mediaId); if (mediaIndex !== -1) goTo(mediaIndex); }));
    on(track, 'keydown', event => {
      if (event.target !== track || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      clearTimer();
      goTo(event.key === 'Home' ? 0 : event.key === 'End' ? slides.length - 1 : index + (event.key === 'ArrowRight' ? 1 : -1));
    });
    on(track, 'scroll', () => { if (!frame) frame = requestAnimationFrame(() => { frame = 0; updatePosition(nearestIndex()); }); }, { passive: true });
    on(document, 'visibilitychange', () => { if (document.hidden) clearTimer(); else schedule(); });
    on(document, 'ay:comfortchange', () => { updateControls(); schedule(); });
    on(window, 'pagehide', clearTimer);
    on(motion, 'change', () => { updateControls(); schedule(); });
    let lastWidth = track.clientWidth;
    const resizeObserver = new ResizeObserver(() => { if (lastWidth !== track.clientWidth) { lastWidth = track.clientWidth; goTo(index, false); } });
    resizeObserver.observe(track);
    const visibilityObserver = new IntersectionObserver(entries => { inView = entries[0].isIntersecting; schedule(); });
    visibilityObserver.observe(gallery);
    on(document, 'shopify:section:unload', event => {
      if (event.detail?.sectionId !== config.sectionId) return;
      destroyed = true; clearTimer(); cancelAnimationFrame(frame); resizeObserver.disconnect(); visibilityObserver.disconnect(); controller.abort();
    });

    /* Zoom plein écran */
    const zoom = gallery.querySelector('[data-gallery-zoom-dialog]');
    if (zoom && typeof zoom.showModal === 'function') {
      const scroll = zoom.querySelector('[data-zoom-scroll]');
      on(zoom.querySelector('[data-zoom-close]'), 'click', () => zoom.close());
      on(zoom, 'click', event => { if (event.target === zoom) zoom.close(); });
      gallery.querySelectorAll('[data-gallery-zoom]').forEach(button => on(button, 'click', () => {
        scroll.replaceChildren();
        const current = button.closest('.ay-media-item');
        let target = null;
        images.forEach(slide => {
          const source = slide.querySelector('[data-gallery-zoom]');
          const alt = slide.querySelector('img')?.alt || '';
          const img = document.createElement('img');
          img.src = source.dataset.zoomSrc; img.alt = alt; img.loading = 'lazy'; img.decoding = 'async';
          scroll.append(img);
          if (slide === current) target = img;
        });
        zoom.showModal();
        if (target) requestAnimationFrame(() => target.scrollIntoView({ block: 'start', behavior: 'instant' }));
      }));
    }

    if (controls) controls.hidden = slides.length < 2;
    const initialIndex = slides.findIndex(slide => slide.dataset.mediaId === String(config.initialMediaId));
    goTo(initialIndex === -1 ? 0 : initialIndex, false);
    updateControls();
    return {
      showMedia(mediaId) { clearTimer(); const mediaIndex = slides.findIndex(slide => slide.dataset.mediaId === String(mediaId)); if (mediaIndex !== -1) goTo(mediaIndex); schedule(); },
      filterVariant: filterVariantMedia
    };
  })();

  /* ------------------------------------------------------------------ */
  /* Variantes (pastilles radio)                                         */
  /* ------------------------------------------------------------------ */
  const form = root.querySelector('[data-product-form]');
  const groups = [...form.querySelectorAll('[data-option-group]')];
  const input = form.querySelector('[data-variant-id]');
  const button = form.querySelector('[data-add-button]');
  const sticky = root.querySelector('[data-sticky-atc]');
  const stickyButton = sticky?.querySelector('[data-sticky-button]');
  const stickyPrice = sticky?.querySelector('[data-sticky-price]');
  const choices = () => groups.map(group => group.querySelector('[data-option-select]:checked')?.value ?? '');
  const findVariant = values => groups.length ? variants.find(item => item.options.every((value, i) => value === values[i])) : variants[0];
  function syncSticky() {
    if (!sticky || !stickyButton) return;
    stickyButton.disabled = button.disabled;
    stickyButton.textContent = button.textContent.trim();
    stickyPrice.textContent = root.querySelector('[data-product-price]').textContent;
  }
  function markAvailability() {
    if (!groups.length) return;
    const current = choices();
    groups.forEach((group, groupIndex) => {
      group.querySelectorAll('.ay-option__value').forEach(wrapper => {
        const radio = wrapper.querySelector('input');
        const candidate = current.slice(); candidate[groupIndex] = radio.value;
        const match = variants.find(item => item.options.every((value, i) => i === groupIndex ? value === radio.value : (current[i] === '' || value === current[i])));
        wrapper.classList.toggle('is-unavailable', config.lazyVariants ? false : !(match && match.available));
      });
      const selected = group.querySelector('[data-option-selected]');
      if (selected) selected.textContent = current[groupIndex];
    });
  }
  let pendingVariantRequest = null;
  async function loadVariant(optionValues) {
    if (findVariant(choices())) { sync(true); return; }
    pendingVariantRequest?.abort();
    const request = new AbortController();
    pendingVariantRequest = request;
    button.disabled = true; button.setAttribute('aria-busy', 'true'); syncSticky();
    const pageUrl = new URL(location.href);
    pageUrl.searchParams.delete('variant');
    pageUrl.searchParams.set('option_values', optionValues.join(','));
    const fetchUrl = new URL(pageUrl.toString());
    fetchUrl.searchParams.set('section_id', config.sectionId);
    try {
      const response = await fetch(fetchUrl.toString(), { signal: request.signal, headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error('variant');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const data = doc.querySelector('[data-product-variants-json]');
      const found = data ? JSON.parse(data.textContent)[0] : null;
      if (request !== pendingVariantRequest) return;
      if (found && !variants.some(item => item.id === found.id)) variants.push(found);
    } catch (error) {
      if (error.name === 'AbortError') return;
      location.assign(pageUrl.toString());
      return;
    } finally {
      if (request === pendingVariantRequest) { button.removeAttribute('aria-busy'); pendingVariantRequest = null; }
    }
    sync(true);
  }
  function sync(changed) {
    const variant = findVariant(choices());
    const stockNote = root.querySelector('[data-stock-note]');
    if (!variant) {
      root.querySelector('[data-product-compare]').hidden = true; input.disabled = true; button.disabled = true; button.textContent = labels.unavailable;
      stockNote.textContent = labels.unavailable; stockNote.dataset.state = 'out';
      root.querySelector('[data-preorder-note]').hidden = true; syncSticky(); markAvailability(); return;
    }
    const preorder = variant.available && (config.preorderTag || (variant.inventory_management && variant.inventory_quantity <= 0));
    input.disabled = false; input.value = variant.id;
    const quantity = form.querySelector('input[name="quantity"]');
    const rule = variant.quantity_rule || {};
    const minimum = Number(rule.min || 1), step = Number(rule.increment || 1);
    const stockMaximum = variant.inventory_management && variant.inventory_policy === 'deny' ? Math.max(0, Number(variant.inventory_quantity)) : Infinity;
    const maximum = Math.min(rule.max == null ? Infinity : Number(rule.max), stockMaximum);
    quantity.min = minimum;
    quantity.step = step;
    if (Number.isFinite(maximum)) quantity.max = maximum; else quantity.removeAttribute('max');
    quantity.value = Math.max(minimum, Math.min(maximum, minimum + Math.floor((Number(quantity.value || minimum) - minimum) / step) * step));

    root.querySelector('[data-product-price]').textContent = formatPrice(variant.price);
    const comparison = root.querySelector('[data-product-compare]');
    comparison.hidden = !(variant.compare_at_price > variant.price);
    root.querySelector('[data-product-compare-value]').textContent = variant.compare_at_price ? formatPrice(variant.compare_at_price) : '';
    const limited = variant.inventory_management && variant.inventory_quantity <= 2 && !config.simpleStock;
    stockNote.textContent = !variant.available ? labels.out : preorder ? labels.preorder : limited ? labels.limited : labels.inStock;
    stockNote.dataset.state = !variant.available ? 'out' : limited ? 'limited' : 'in';
    root.querySelector('[data-preorder-note]').hidden = !preorder;
    button.disabled = !variant.available;
    button.textContent = !variant.available ? labels.sold : preorder ? labels.preorderButton : labels.add;
    syncSticky();
    markAvailability();
    if (changed) {
      const url = new URL(location.href); url.searchParams.set('variant', variant.id); history.replaceState({}, '', url);
      if (config.colorIndex !== -1) carousel.filterVariant(variant.options[config.colorIndex]);
      if (variant.media_id) carousel.showMedia(variant.media_id);
    }
  }
  form.addEventListener('change', event => {
    if (!event.target.matches('[data-option-select]')) return;
    if (config.lazyVariants) {
      const optionValues = groups.map(group => group.querySelector('[data-option-select]:checked')?.dataset.optionValueId);
      if (optionValues.every(Boolean)) { loadVariant(optionValues); return; }
    }
    sync(true);
  });
  sync(false);
  const initialVariant = variants.find(item => item.id === Number(input.value)) || variants[0];
  if (initialVariant && config.colorIndex !== -1) carousel.filterVariant(initialVariant.options[config.colorIndex]);

  /* Bouton d'achat fixe (mobile) */
  if (sticky && stickyButton && 'IntersectionObserver' in window) {
    stickyButton.addEventListener('click', () => { if (!button.disabled) button.click(); });
    const mobile = window.matchMedia('(max-width: 899px)');
    let mainVisible = true;
    const toggleSticky = () => {
      const show = mobile.matches && !mainVisible;
      sticky.classList.toggle('is-visible', show);
      sticky.inert = !show;
      sticky.setAttribute('aria-hidden', String(!show));
      document.body.classList.toggle('ay-sticky-atc-on', show);
    };
    new IntersectionObserver(([entry]) => { mainVisible = entry.isIntersecting; toggleSticky(); }).observe(button);
    mobile.addEventListener('change', toggleSticky);
  }

  /* Mot cadeau */
  root.querySelectorAll('[data-gift-note-toggle]').forEach(toggle => {
    const wrap = toggle.closest('.ay-gift-note');
    const field = wrap?.querySelector('[data-gift-note-field]');
    const textarea = wrap?.querySelector('.ay-gift-note__textarea');
    toggle.addEventListener('change', () => { if (!field || !textarea) return; field.hidden = !toggle.checked; textarea.disabled = !toggle.checked; if (toggle.checked) textarea.focus(); else textarea.value = ''; });
  });

  /* Avis Judge.me : n'afficher le bloc que lorsqu'il a du contenu */
  (() => {
    const wrap = document.getElementById('ay-pdp-reviews');
    const widget = wrap?.querySelector('.jdgm-review-widget');
    if (!wrap || !widget) return;
    const check = () => { if (widget.querySelector('.jdgm-rev, .jdgm-rev-widg__reviews')) { wrap.hidden = false; return true; } return false; };
    if (check()) return;
    const mo = new MutationObserver(() => { if (check()) mo.disconnect(); });
    mo.observe(widget, { childList: true, subtree: true });
    window.setTimeout(() => mo.disconnect(), 9000);
  })();

  /* Description : chaque H2/H3 devient un sous-accordéon */
  (() => {
    const headings = /^(H2|H3)$/;
    root.querySelectorAll('[data-product-description]').forEach(description => {
      if (!Array.from(description.children).some(node => headings.test(node.tagName))) return;
      const fragment = document.createDocumentFragment();
      const intro = document.createElement('div'); intro.className = 'ay-product-description__intro'; fragment.appendChild(intro);
      let target = intro;
      Array.from(description.childNodes).forEach(node => {
        if (node.nodeType === Node.ELEMENT_NODE && headings.test(node.tagName)) {
          const part = document.createElement('details'); part.className = 'ay-product-description__part'; part.setAttribute('open', '');
          const summary = document.createElement('summary'); summary.textContent = node.textContent.trim();
          const content = document.createElement('div'); content.className = 'ay-product-description__part-content';
          part.append(summary, content); fragment.appendChild(part); target = content; return;
        }
        target.appendChild(node);
      });
      if (!intro.textContent.trim() && !intro.children.length) intro.remove();
      description.replaceChildren(fragment);
    });
  })();

  /* Sleek Bundles : libellés localisés sans changer les valeurs de variantes */
  (() => {
    const english = (window.ayRoutes?.locale || 'fr').toLowerCase().startsWith('en');
    const names = {
      'Pantalon bébé en coton biologique — Tout Doux™': 'Organic Cotton Baby Pants — Tout Doux™',
      'Barboteuse à manches courtes en gaze de coton — Tout Doux™': 'Short-Sleeve Cotton Gauze Romper — Tout Doux™',
      'Barboteuse sans manches en gaze de coton bio — Tout Doux™': 'Sleeveless Organic Cotton Gauze Romper — Tout Doux™',
      'Bavoir bandana en mousseline de coton biologique — Tout Doux™': 'Organic Cotton Muslin Bandana Bib — Tout Doux™',
      'Cache-couche kimono en coton biologique — Tout Doux™': 'Organic Cotton Kimono Baby Bodysuit — Tout Doux™',
      'Bonnet noué en coton biologique — Tout Doux™': 'Organic Cotton Knotted Baby Hat — Tout Doux™',
      'Doudou animal en mousseline — MINUS by A&Y™': 'Muslin Animal Comforter — MINUS by A&Y™',
      'Couverture d’emmaillotage en mousseline 100 % coton — MINUS by A&Y™': '100% Cotton Muslin Swaddle Blanket — MINUS by A&Y™'
    };
    const colours = { 'vert sauge': 'Sage green', vert: 'Green', blanc: 'White', jaune: 'Yellow', beige: 'Beige', 'bleu doux': 'Soft blue', 'bleu clair': 'Light blue', 'rose poudré': 'Dusty pink', 'rose poudre': 'Dusty pink', 'pingouin bleu-gris': 'Blue-grey penguin', 'tigre ocre': 'Ochre tiger', 'panda blanc': 'White panda', 'lapin rose': 'Pink rabbit', 'sable nordique': 'Nordic sand', 'brume nordique': 'Nordic mist', 'neige d’ivoire': 'Ivory snow', 'sauge boréale': 'Boreal sage', 'rose d’argile': 'Clay pink' };
    function label(text, element) {
      const trimmed = text.trim();
      let result = trimmed;
      if (element.closest('select')) {
        if (/^(Choose a |Choisir )/i.test(trimmed)) {
          const size = /taille|size/i.test(trimmed), colour = /couleur|colou?r/i.test(trimmed);
          result = english ? (size && colour ? 'Choose colour and size' : size ? 'Choose size' : 'Choose colour') : (size && colour ? 'Choisir la couleur et la taille' : size ? 'Choisir la taille' : 'Choisir la couleur');
        } else if (english) {
          result = trimmed.replace(/^([^(/]+)(.*)$/, (_, colour, tail) => (colours[colour.trim().toLowerCase()] || colour.trim()) + (tail ? ' ' + tail.trim() : ''));
        }
      } else {
        if (trimmed.includes(' / ') && (['Livraison suivie / Tracked shipping', 'Paiement sécurisé / Secure checkout', 'Coton biologique / Organic cotton', 'Précommande / Pre-order', 'Ajouter la sélection / Add selection'].includes(trimmed) || element.closest('.sb-sg-title,.sb-sg-sub,.sb-pb-subtitle,.sb-kr-onm,.sb-kr-ods,.sb-cta-lbl'))) {
          const parts = trimmed.split(' / ');
          if (parts.length === 2) result = parts[english ? 1 : 0];
        }
        if (english && names[result]) result = names[result];
        if (!english && result === 'Standard price') result = 'Prix à l’unité';
      }
      return text.replace(trimmed, result);
    }
    const bound = new WeakSet();
    function attach(node) {
      if (bound.has(node)) return;
      bound.add(node);
      let pending = false;
      function translate() {
        pending = false;
        const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT);
        let current;
        while ((current = walker.nextNode())) {
          const parent = current.parentElement;
          if (!parent || parent.closest('script,style') || !current.nodeValue.trim()) continue;
          const value = label(current.nodeValue, parent);
          if (value !== current.nodeValue) current.nodeValue = value;
        }
        node.querySelectorAll('select.sb-variant').forEach(select => { const text = label(select.getAttribute('aria-label') || '', select); if (text && text !== select.getAttribute('aria-label')) select.setAttribute('aria-label', text); });
        if (english) node.querySelectorAll('img[alt]').forEach(img => { if (names[img.alt]) img.alt = names[img.alt]; });
      }
      new MutationObserver(() => { if (!pending) { pending = true; requestAnimationFrame(translate); } }).observe(node, { childList: true, subtree: true, characterData: true });
      translate();
    }
    document.querySelectorAll('.sleekbundles-widget').forEach(attach);
    new MutationObserver(records => records.forEach(record => record.addedNodes.forEach(added => {
      if (added.nodeType !== 1) return;
      if (added.matches('.sleekbundles-widget')) attach(added);
      added.querySelectorAll('.sleekbundles-widget').forEach(attach);
    }))).observe(document.body, { childList: true, subtree: true });
  })();
})();
